import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 10 * 60 * 1000;
const SNOOZE_MS = 30 * 60 * 1000;
const VERSION_URL = '/app-version.json';
const SNOOZE_KEY = 'version_check_snoozed_until';

interface UseVersionCheckResult {
    updateAvailable: boolean;
    reload: () => void;
    snooze: () => void;
}

const noop = () => {
    /* no-op */
};

export function useVersionCheck(): UseVersionCheckResult {
    const enabled = import.meta.env.PROD && __APP_VERSION__ !== 'unknown' && __APP_VERSION__ !== '';
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const intervalRef = useRef<number | null>(null);

    const clearPoll = useCallback(() => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const check = useCallback(async () => {
        try {
            const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
                cache: 'no-store',
                credentials: 'omit',
            });
            if (!res.ok) return;
            const json = (await res.json()) as { version?: string };
            const remote = json?.version;
            if (!remote || remote === 'unknown' || remote === __APP_VERSION__) return;
            const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
            if (Date.now() < snoozedUntil) return;
            setUpdateAvailable(true);
        } catch {
            // silent: network/parse failure should not surface to the user
        }
    }, []);

    useEffect(() => {
        if (!enabled || updateAvailable) {
            clearPoll();
            return;
        }
        check();
        intervalRef.current = window.setInterval(check, POLL_INTERVAL_MS);
        return clearPoll;
    }, [enabled, updateAvailable, check, clearPoll]);

    useEffect(() => {
        if (!enabled) return;
        const handler = () => {
            if (document.visibilityState === 'visible') check();
        };
        document.addEventListener('visibilitychange', handler);
        window.addEventListener('focus', handler);
        return () => {
            document.removeEventListener('visibilitychange', handler);
            window.removeEventListener('focus', handler);
        };
    }, [enabled, check]);

    const reload = useCallback(() => {
        localStorage.removeItem(SNOOZE_KEY);
        window.location.reload();
    }, []);

    const snooze = useCallback(() => {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
        setUpdateAvailable(false);
    }, []);

    if (!enabled) {
        return { updateAvailable: false, reload: noop, snooze: noop };
    }

    return { updateAvailable, reload, snooze };
}
